import SuperSelectField from '@hashicorp/react-mds/src/hds/components/form/super-select/field'
import type {
	SuperSelectOption,
	SuperSelectOptionProps,
	SuperSelectSearchInputProps,
} from '@hashicorp/react-mds/src/hds/components/form/super-select'
import Field from '@hashicorp/react-mds/src/hds/components/form/field'
import { TextInputBase } from '@hashicorp/react-mds/src/hds/components/form/text-input'
import { useFormContext, Controller } from 'react-hook-form'
import { useEffect, useState, useCallback, useRef } from 'react'
import { EducationResourceItem, EducationQuestions } from 'lib/careers/types'
import { isEducationResourceResponse } from 'lib/careers/greenhouse-careers'
import s from '../style.module.css'

interface BaseEducationDropdownProps {
	id: string
	label: string
	listLabel: string
	isRequired: boolean
	options: SuperSelectOption[]
	onScroll?: (e: React.UIEvent<HTMLUListElement>) => void
	searchProps?: SuperSelectSearchInputProps
}

function BaseEducationDropdown({
	id,
	label,
	listLabel,
	isRequired,
	options,
	onScroll,
	searchProps,
}: BaseEducationDropdownProps) {
	const { control, formState } = useFormContext()
	const errorMsg = formState.errors[id]?.message?.toString()

	return (
		<Controller
			control={control}
			name={id}
			defaultValue={''}
			render={({ field }) => (
				<SuperSelectField
					label={label}
					error={errorMsg}
					isRequired={isRequired}
					listLabel={listLabel}
					options={options}
					onChangeSelectedOption={(option) => {
						field.onChange(option.value)
					}}
					onSearchBlur={field.onBlur}
					searchProps={searchProps}
					onScroll={onScroll}
				/>
			)}
		/>
	)
}

/**
 * Despite all of the education resources having the same API, we have 2 components for education questions:
 * {@link ClientSideSearchDropdown} built with degrees and disciplines in mind, these are small datasets and it makes sense to handle search client-side and get everything in a single request
 * {@link ServerSideSearchDropdown} makes use of the pagination and uses the server-side search since schools is a much larger dataset
 */

const fmtToOptions = (items: EducationResourceItem[]): SuperSelectOption[] => {
	return items.map((item) => ({
		id: item.id,
		label: item.text,
		value: item.id,
	}))
}

const loadingOption: SuperSelectOptionProps = {
	id: 'loading',
	label: 'Loading...',
	value: 'loading',
	isLoading: true,
}

export interface ServerSideSearchDropdownProps {
	question: EducationQuestions[2]
}

export function ServerSideSearchDropdown({
	question,
}: ServerSideSearchDropdownProps) {
	const [items, setItems] = useState<EducationResourceItem[]>([])
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [hasMorePages, setHasMorePages] = useState<boolean>(false)
	const [searchTerm, setSearchTerm] = useState<string>('')
	const currentPageRef = useRef(1)

	const fetchItems = async (page: number = 1) => {
		setIsLoading(true)

		try {
			const urlStr = `https://boards-api.greenhouse.io/v1/boards/hashicorp/education/${question.apiResource}`
			const url = new URL(urlStr)
			url.searchParams.set('page', page.toString())
			if (searchTerm.length > 0) {
				url.searchParams.set('term', searchTerm)
			}

			const res = await fetch(url)
			const data = await res.json()

			if (!isEducationResourceResponse(data)) {
				throw new Error('Invalid response from Greenhouse API')
			}

			const hasMore = data.meta.total_count > page * data.meta.per_page
			setHasMorePages(hasMore)
			return data
		} catch (error) {
			console.error(`Error fetching ${question.apiResource}:`, error)
			throw error
		} finally {
			setIsLoading(false)
		}
	}

	const loadMore = useCallback(async () => {
		if (isLoading || !hasMorePages) return

		try {
			currentPageRef.current += 1
			const data = await fetchItems(currentPageRef.current)
			setItems((prev) => [...prev, ...data.items])
		} catch (error) {
			console.error(`Error loading more ${question.apiResource}:`, error)
		}
	}, [isLoading, hasMorePages])

	const handleScroll = useCallback(
		(e: React.UIEvent<HTMLUListElement>) => {
			const element = e.currentTarget
			const distanceFromBottom =
				element.scrollHeight - (element.scrollTop + element.clientHeight)
			const triggerDistance = element.clientHeight * 0.2

			if (distanceFromBottom <= triggerDistance) {
				loadMore()
			}
		},
		[loadMore]
	)

	useEffect(() => {
		fetchItems().then((data) => {
			setItems(data.items)
			setHasMorePages(data.meta.total_count > data.meta.per_page)
		})
	}, [])

	const DEBOUNCE_TIME = 200
	useEffect(() => {
		const handler = setTimeout(() => {
			currentPageRef.current = 1
			fetchItems(currentPageRef.current).then((data) => {
				setItems(data.items)
				const hasMore = data.meta.total_count > data.meta.per_page
				setHasMorePages(hasMore)
			})
		}, DEBOUNCE_TIME)

		return () => clearTimeout(handler)
	}, [searchTerm])

	const options = isLoading
		? [...fmtToOptions(items), loadingOption]
		: fmtToOptions(items)

	return (
		<BaseEducationDropdown
			id={question.id}
			label={question.label}
			listLabel={question.apiResource}
			isRequired={question.required}
			options={options}
			onScroll={handleScroll}
			searchProps={{
				placeholder: `Search for a ${question.label}`,
				value: searchTerm,
				onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
					setSearchTerm(e.target.value),
			}}
		/>
	)
}

export interface ClientSideSearchDropdownProps {
	question: EducationQuestions[0 | 1]
}

export function ClientSideSearchDropdown({
	question,
}: ClientSideSearchDropdownProps) {
	const [items, setItems] = useState<EducationResourceItem[]>([])
	const [filteredItems, setFilteredItems] = useState<EducationResourceItem[]>(
		[]
	)
	const [isLoading, setIsLoading] = useState<boolean>(false)

	const fetchItems = useCallback(async () => {
		setIsLoading(true)
		try {
			const res = await fetch(
				`https://boards-api.greenhouse.io/v1/boards/hashicorp/education/${question.apiResource}`
			)
			const data = await res.json()
			if (!isEducationResourceResponse(data)) {
				throw new Error('Invalid response from API')
			}
			setItems(data.items)
			setFilteredItems(data.items)
		} catch (error) {
			console.error(`Error fetching ${question.apiResource}:`, error)
		} finally {
			setIsLoading(false)
		}
	}, [question.apiResource])

	// initial fetch
	useEffect(() => {
		fetchItems()
	}, [fetchItems])

	const handleSearch = useCallback(
		(term: string) => {
			const searchLower = term.toLowerCase()
			const filtered = items.filter((item) =>
				item.text.toLowerCase().includes(searchLower)
			)
			setFilteredItems(filtered)
		},
		[items]
	)

	const options = isLoading ? [loadingOption] : fmtToOptions(filteredItems)

	return (
		<BaseEducationDropdown
			id={question.id}
			label={question.label}
			listLabel={question.apiResource}
			isRequired={question.required}
			options={options}
			searchProps={{
				placeholder: `Search for a ${question.label}`,
				onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
					handleSearch(e.target.value),
			}}
		/>
	)
}

interface DateRangeInputsProps {
	question: EducationQuestions[3 | 4]
}

export const DateRangeInputs = ({ question }: DateRangeInputsProps) => {
	const { register, formState } = useFormContext()
	const errorMsg = formState.errors[question.id]?.message?.toString()

	const currentYear = new Date().getFullYear()
	const minYear = currentYear - question.minYearDelta
	const maxYear = currentYear + question.maxYearDelta
	const minDate = `${minYear}-01` // January of min year
	const maxDate = `${maxYear}-12` // December of max year

	return (
		<Field
			id={question.id}
			label={question.label}
			className={s.dateRangeInputs}
			error={errorMsg}
			isRequired={question.required}
		>
			<TextInputBase
				size="large"
				type="month"
				field={{
					...register(question.id, {
						required: question.required,
					}),
					min: minDate,
					max: maxDate,
				}}
			/>
		</Field>
	)
}
